<template>
  <nav v-if="!disablePagination" class="flex flex-wrap items-center justify-between gap-3 px-1 md:w-full">
    <Flex gap-3 items-center class="min-w-1/2">
      <div class="text-cockpit-muted">
        Exibindo
        <b>{{ currentStart }}</b> a <b>{{ currentEnd }}</b> de
        <b>{{ totalItems }}</b> itens.
      </div>
      <slot name="left" />
    </Flex>
    <Flex gap-8 items-center justify-end class="min-w-1/2">
      <SelectTable v-model="modelLimit" :items="statusItems" up class="w-28" placeholder="" v-if="totalItems > 20" />

      <ul v-if="totalPages > 1" class="inline-flex items-center h-10 rounded-lg border border-cockpit-line divide-x divide-cockpit-line text-cockpit-muted">
        <li :class="{ [disableClass]: currentPage == 1 }" @click="previousPage">
          <PaginationButton class="border-e-0 border-slate-300 rounded-s-lg w-[60px]" icon="fa-solid fa-chevron-left mx-2" />
        </li>

        <div v-if="totalPages < 4" class="flex items-center divide-x divide-cockpit-line">
          <li v-for="page in totalPages" :key="page" @click="goToPage(page)">
            <PaginationButton :class="currentPage === page ? activeClass : borderClass">
              {{ page }}
            </PaginationButton>
          </li>
        </div>

        <div v-else class="flex items-center divide-x divide-cockpit-line">
          <li @click="goToPage(1)">
            <PaginationButton :class="currentPage === 1 ? activeClass : borderClass">1</PaginationButton>
          </li>

          <li v-if="currentPage > 3">
            <PaginationButton>...</PaginationButton>
          </li>

          <li v-if="currentPage > 2" @click="goToPage(currentPage - 1)">
            <PaginationButton :class="borderClass">{{ currentPage - 1 }}</PaginationButton>
          </li>

          <li v-if="currentPage !== 1 && currentPage !== totalPages" @click="goToPage(currentPage)">
            <PaginationButton :class="activeClass">{{ currentPage }}</PaginationButton>
          </li>

          <li v-if="currentPage < totalPages - 1" @click="goToPage(currentPage + 1)">
            <PaginationButton :class="borderClass">{{ currentPage + 1 }}</PaginationButton>
          </li>

          <li v-if="currentPage < totalPages - 2">
            <PaginationButton>...</PaginationButton>
          </li>

          <li v-if="lastPage" @click="goToPage(lastPage)">
            <PaginationButton :class="currentPage === lastPage ? activeClass : borderClass">
              {{ lastPage }}
            </PaginationButton>
          </li>
        </div>

        <li :class="{ [disableClass]: currentPage == lastPage }" @click="nextPage">
          <PaginationButton class="border-s-0 rounded-e-lg border-slate-300 w-[60px]" icon="fa-solid fa-chevron-right mx-2" />
        </li>
      </ul>
    </Flex>
  </nav>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'

const emit = defineEmits(['update:modelValue', 'update:limit'])

const props = defineProps({
  disablePagination: {
    type: Boolean,
    default: false,
  },
  limit: {
    type: Number,
    default: 10,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    default: 0,
  },
  modelValue: {
    type: Number,
    default: 1,
  },
})

const activeClass = 'bg-cockpit-green text-cockpit-canvas'
const borderClass = 'hover:bg-cockpit-green/10 hover:text-cockpit-green'
const disableClass = 'opacity-50'

const currentPage = ref(1)
const modelLimit = ref(props.limit)
const statusItems = ref([
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: '200', value: 200 },
  { label: '500', value: 500 },
])

const lastPage = computed(() => props.totalPages)
const currentStart = computed(() => {
  if (!props.totalItems) return 0
  return currentPage.value * modelLimit.value - modelLimit.value + 1
})
const currentEnd = computed(() => {
  if (!props.totalItems) return 0
  return Math.min(currentPage.value * modelLimit.value, props.totalItems)
})

const goToPage = (pageNumber) => {
  let page = Number(pageNumber)
  if (!Number.isFinite(page) || page <= 0 || page > props.totalPages) {
    page = 1
  }
  currentPage.value = page
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    currentPage.value += 1
  }
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1
  }
}

watch(
  () => props.modelValue,
  (value) => {
    currentPage.value = value
  },
)

watch(currentPage, (value) => {
  emit('update:modelValue', value)
})

watch(
  () => modelLimit.value,
  (value) => {
    emit('update:limit', value)
  },
)

onMounted(() => {
  currentPage.value = props.modelValue
})
</script>
